import { ChevronRight, Pencil, Plus, RotateCcw, Send, Trash2, Upload, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate , useSearchParams } from "react-router-dom";
import { CsvImportDialog } from "../components/CsvImportDialog";
import { SendIntakeDialog } from "../components/SendIntakeDialog";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import { clients as clientsApi, type ClientListItem, type ClientStatus } from "../lib/api";
import { clientInitials } from "../lib/clientName";
import { EmptyState } from "../components/EmptyState";

const STATUS_OPTIONS: { value: ClientStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "completed", label: "Completed" },
  { value: "churned", label: "Deleted" },
];

export default function ClientsPage() {
  const nav = useNavigate();
  const [items, setItems] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Seed from ?q= so the top-bar search (and shared links) land here filtered —
  // and KEEP following it: submitting the top-bar search while already on this
  // page updates the URL, so the state must track the param, not just read it
  // once at mount (back/forward navigation gets the same treatment).
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("q") ?? "");
  useEffect(() => {
    // Missing param counts as empty: navigating back from /clients?q=foo to
    // plain /clients must CLEAR the search and refetch, not keep stale results.
    const q = searchParams.get("q") ?? "";
    if (q !== search) { setSearch(q); load(q); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [status, setStatus] = useState<ClientStatus | "all">("all");
  const [importOpen, setImportOpen] = useState(false);
  const [sendIntake, setSendIntake] = useState<ClientListItem | null>(null);

  async function load(qOverride?: string) {
    setLoading(true); setError(null);
    try {
      const res = await clientsApi.list({
        status: status === "all" ? undefined : status,
        search: (qOverride ?? search) || undefined,
        limit: 100,
      });
      setItems(res.items);
    } catch (e: any) {
      setError(e?.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <Shell
      title="Clients"
      subtitle="Pipeline"
      actions={
        <>
          <button className="ghost-button" onClick={() => setImportOpen(true)}>
            <Upload size={16} aria-hidden="true" /> Import CSV
          </button>
          <button className="primary-button" onClick={() => nav("/clients/new")}>
            <Plus size={16} aria-hidden="true" /> New Client
          </button>
        </>
      }
    >
      <div className="panel">
        <div className="filters-row">
          <input
            className="search-input"
            placeholder="Search by name, email, phone…"
            aria-label="Search by name, email, or phone"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            aria-label="Filter by status"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button className="ghost-button" onClick={() => load()}>Search</button>
        </div>

        {error && <p className="error-text">{error}</p>}
        {status === "churned" && (
          <p className="muted small" role="note" style={{ margin: "6px 0 10px" }}>
            Deleted clients sit here for a 24-hour grace window — restore them anytime before that, or use
            &ldquo;Delete forever&rdquo; to erase immediately. After 24 hours they are permanently erased automatically.
          </p>
        )}
        {loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="client-table">
            <div className="client-table-header">
              <span>Client</span>
              <span>Contact</span>
              <span>Status</span>
              <span>Actions</span>
            </div>
            {items.length === 0 && (
              <EmptyState
                icon={<UsersRound size={26} />}
                title="No clients match"
                hint="Try clearing the search or status filter — or add a new client to get started."
              />
            )}
            {items.map((c) => (
              <div key={c.id} className="client-table-row">
                <button className="link-cell" onClick={() => nav(`/clients/${c.id}`)}>
                  <div className="avatar" title={c.legal_name ?? undefined}>{clientInitials(c.legal_name)}</div>
                  <div>
                    <strong>{c.legal_name ?? "(unnamed)"}</strong>
                    <span>{c.id.slice(0, 8)} · {c.assigned_agent || "Unassigned"}</span>
                  </div>
                </button>
                <div className="contact-cell">
                  <span>{c.email}</span>
                  <span className="muted small">{c.phone}</span>
                </div>
                <StatusPill status={c.status} />
                <div className="row-actions">
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => setSendIntake(c)}
                    title="Send intake link"
                    aria-label="Send intake link"
                  >
                    <Send size={14} aria-hidden="true" />
                  </button>
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => nav(`/clients/${c.id}`)}
                    title="Edit"
                    aria-label="Edit"
                  >
                    <Pencil size={14} aria-hidden="true" />
                  </button>
                  {c.status === "churned" && (
                    <button
                      className="icon-button icon-button--sm"
                      title="Restore"
                      aria-label="Restore"
                      onClick={async () => {
                        try {
                          await clientsApi.update(c.id, { status: "pending" } as any);
                          setItems(prev => prev.filter(x => x.id !== c.id));
                        } catch (e: any) { alert(`Restore failed: ${e?.message || "unknown error"}`); }
                      }}
                    >
                      <RotateCcw size={14} aria-hidden="true" />
                    </button>
                  )}
                  <button
                    className="icon-button icon-button--sm"
                    title={c.status === "churned" ? "Delete forever" : "Delete"}
                    aria-label={c.status === "churned" ? "Delete forever" : "Delete"}
                    style={{ color: "var(--ne-red)" }}
                    onClick={async () => {
                      const hard = c.status === "churned";
                      const msg = hard
                        ? `PERMANENTLY erase ${c.legal_name ?? "this client"}? Everything goes with them — signed contracts, reports, uploads, history. This cannot be undone.`
                        : `Delete ${c.legal_name ?? "this client"}? They move to the Deleted list, where you can restore them — after 24 hours they are permanently erased.`;
                      if (!confirm(msg)) return;
                      try {
                        if (hard) await clientsApi.removeForever(c.id);
                        else await clientsApi.remove(c.id);
                        setItems(prev => prev.filter(x => x.id !== c.id));
                      } catch (e: any) {
                        alert(`Delete failed: ${e?.message || "unknown error"}`);
                      }
                    }}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                  </button>
                  <button
                    className="icon-button icon-button--sm"
                    onClick={() => nav(`/clients/${c.id}`)}
                    title="Open"
                    aria-label="Open"
                  >
                    <ChevronRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={() => load()}
      />
      {sendIntake && (
        <SendIntakeDialog
          open={true}
          onClose={() => setSendIntake(null)}
          clientId={sendIntake.id}
          defaultEmail={sendIntake.email}
          defaultPhone={sendIntake.phone}
        />
      )}
    </Shell>
  );
}
